import { App } from '@Modules/App';
import { UI } from '@Modules/UI';
import { User } from '@Modules/User';
import { SortFilter } from '@Modules/SortFilter';
import { appConfig as $config } from '@App/app.config';
import { Artworks } from '../index';
import { mapping } from './mapping';
import { artworkType, colorGroups, printArea } from './constants';
import { CUSTOM_EVENTS } from '../../../../../../../client/constants/communication-custom-events';
import { ID } from '../../../../../../../client/util/ID';
import { canvas } from './canvas';
import { loadFont } from './utils';

const applyAllRatioDeviation = 0.1;

const defaultState = {
  template: null,
  parentTemplateName: null,
  imageAssetIds: [],
  patternAssetId: null,
  height: 1000,
  backgroundColor: 'white',
  selectedArtworks: [],
  selectedPattern: null,
  printArea: printArea[0].value,
  ratio: 1,
  scalePatternToFill: false,
};

const defaultArtworkState = {
  x: 0,
  y: 0,
  w: null,
  h: null,
  a: 0,
  imagePlacement: 'Center',
  rotate: true,
};

const defaultText = 'Add your text here';

const GRAVITY = {
  LEFT: 'Left',
  CENTER: 'Center',
};

const defaultTextState = {
  fontSize: 100,
  text: '',
  x: 0,
  y: 0,
  w: null,
  h: null,
  fontFamily: null,
  fontColor: 'black',
  isPersonalization: false,
  gravity: GRAVITY.LEFT,
  moved: false,
};

export const complexArtwork = (function () {
  let $artwork;
  let $callbacks;
  let $canvasXhr;
  let $isOpenModalDisabled = false;
  let $artworkAwaitingGenerating = null;
  let $isPngFromVectorAwaiting = false;
  let $patternAwaitingGenerating = null;
  let $isDtg = false;
  let responseMemo = {};

  let state = { ...defaultState };
  let artworkStates = [];
  let textStates = [];

  let init = (artwork, callbacks) => {
    const defaults = App.pageData('currenteditedcontent.defaults');
    defaultTextState.fontFamily = defaults?.SystemFontFamilyCollection?.[0]?.Font;

    mapping.prepareComplexArtwork(artwork);

    $artwork = { ...artwork };
    $isDtg = artwork.isDtg;

    setAopTemplatesSelector();

    $callbacks = callbacks;

    state = {
      ...defaultState,
      template: defaults?.AopTemplates?.[0] || null,
      parentTemplateName: null,
      printArea: artwork.printArea,
    };

    setArtworkOnInit(artwork);
    setTextOnInit(artwork);

    setTemplate(defaults?.AopTemplates?.[0]);

    initRatioSlider();
    initHandlers(artwork);
  };

  let initHandlers = (artwork) => {
    $('#page')
      .on('click', '.js-action-isDtg', isDtgChangeHandler)
      .on('click', '.js-action-addArtwork', addArtworkOrPattern.bind(null, false))
      .on('click', '.js-action-addPattern', addArtworkOrPattern.bind(null, true))
      .on('click', '.js-action-addText', addText)
      .on('click', '#filledPattern .js-action-delete', deleteArtworkOrPattern.bind(null, true))
      .on('change', '[name="artworkBackgroundColor"]', colorChangedHandler)
      .on('click', '.js-action-apply-all', applyAllModal);

    $('.artwork-template-control').select2({
      minimumResultsForSearch: 0,
      templateResult: templateResult,
    }).on('select2:select', templateChangedHandler);
    $('.artwork-print-area-control').select2().on('select2:select', printAreaChangedHandler);

    $('#filledPattern')
      .on('change', '.kt-checkbox.js-action-upscale-to-fill', upscaleToFillChangedHandler);

    loadCanvasPreview(true);

    Coloris({ el: '[name="artworkBackgroundColor"]' });

    if (artwork.isDtg) {
      state = { ...state, backgroundColor: 'white' };

      $('.complex-artwork-file [name="artworkBackgroundColor"]').val(state.backgroundColor).prop('disabled', true);
      document.querySelector('.complex-artwork-file [name="artworkBackgroundColor"]').dispatchEvent(new Event('input', { bubbles: true }));
    }

    window.addEventListener(CUSTOM_EVENTS.FILE_GENERATOR_NOTIFICATION, fileGeneratorNotificationHandler);
  };

  let initHandlersForArtwork = (container) => {
    $(container).on('click', '.js-action-delete', deleteArtworkOrPattern.bind(null, false));

    $('.artwork-placement-control', container)
      .select2({ placeholder: $lang.artwork.complex.Custom })
      .on('select2:select', placementChangedHandler);

    $(container)
      .on('change', '.kt-checkbox.js-action-rotate', rotateChangedHandler)
      .on('change', '.kt-checkbox.js-action-upscale', upscaleChangedHandler);
  };

  let initHandlersForText = (container) => {
    $(container)
      .on('click', '.js-action-delete', deleteText)
      .on('change', '[name="fontColor"]', fontColorChangedHandler)
      .on('change', '[name="text"]', textChangedHandler)
      .on('change', '[name="fontSize"]', fontSizeChangedHandler)
      .on('change', '.js-action-personalization', personalizationChangedHandler)
      .on('change', '.js-action-gravity', gravityChangedHandler);

    $('.artwork-font-control', container).select2().on('select2:select', fontChangedHandler);

    Coloris({ el: '[name="fontColor"]' });
  };

  let initRatioSlider = () => {
    const blockScaleSlider = $('.js-blockScale-div')[0];

    noUiSlider.create(blockScaleSlider, {
      start: 1,
      step: 0.2,
      tooltips: true,
      format: wNumb({ decimals: 1 }),
      range: {
        min: [0.2],
        max: [2]
      },
      pips: {
        mode: 'count',
        values: 4,
        density: 12,
        format: wNumb({ decimals: 1 }),
      }
    });

    blockScaleSlider.noUiSlider.on('change', function (values) {
      ratioChangedHandler(parseFloat(values[0]));
    });
  };

  let clear = () => {
    window.removeEventListener(CUSTOM_EVENTS.FILE_GENERATOR_NOTIFICATION, fileGeneratorNotificationHandler);

    $('.js-moneyRange-div')?.[0]?.noUiSlider?.destroy();

    $artworkAwaitingGenerating = null;

    canvas.destroy();

    $artwork = null;
    $callbacks = null;
    $canvasXhr = null;
    $isOpenModalDisabled = false;
    $artworkAwaitingGenerating = null;
    $isPngFromVectorAwaiting = false;
    $patternAwaitingGenerating = null;
    $isDtg = false;
    responseMemo = {};

    state = { ...defaultState };
    artworkStates = [];
    textStates = [];
  };

  let isDtgChangeHandler = (e) => {
    $isDtg = e.target.checked;

    const colorInput = $('.complex-artwork-file [name="artworkBackgroundColor"]');
    state = { ...state, backgroundColor: 'white' };

    colorInput.val(state.backgroundColor);
    document.querySelector('.complex-artwork-file [name="artworkBackgroundColor"]').dispatchEvent(new Event('input', { bubbles: true }));

    if ($isDtg) {
      App.pageData('currenteditedcontent.backgroundColor', false);
      colorInput.prop('disabled', true);

      return;
    }

    App.pageData('currenteditedcontent.backgroundColor', state.backgroundColor);
    colorInput.prop('disabled', false);

    loadCanvasPreview();
  };

  let addArtworkOrPattern = (isPattern) => {
    if ($isOpenModalDisabled) {
      return;
    }

    $isOpenModalDisabled = true;

    SortFilter.clear('artworks');

    let params = {
      action: 'dialog', withTrims: false, isPattern: isPattern, callback: () => {
        const selectedArtwork = App.pageData('selectArtModal');
        App.pageData('selectArtModal', 'clear');

        if (!selectedArtwork?.id) {
          return;
        }

        if (isPattern) {
          addPattern(selectedArtwork);

          const hasCentralArtwork = (App.pageData('currenteditedcontent.shortArtworks') || [])
            .some(a => a.artworkType === 'Central');

          if (!hasCentralArtwork) {
            fillFormByArtworkData(selectedArtwork);
          }

          return;
        }

        addArtwork(selectedArtwork);

        if (state.selectedArtworks?.length === 1) {
          fillFormByArtworkData(selectedArtwork);
        }
      },
      onHide: () => $isOpenModalDisabled = false,
    };

    Artworks.init(params);
  };

  let addArtwork = (selectedArtwork, skipLoadCanvas, skipVectorCheck) => {
    if (!skipLoadCanvas && !skipVectorCheck && isSourceFileIsVector(selectedArtwork)) {
      isVectorImageQuestion(selectedArtwork);
      return;
    }

    const artworkContainer = $($('#filledCentralArtwork').html());
    artworkContainer.insertBefore($('.js-action-addArtwork'));

    initHandlersForArtwork(artworkContainer);

    setHeaderArtwork(selectedArtwork, artworkContainer);

    state = {
      ...state,
      imageAssetIds: state.imageAssetIds?.concat(selectedArtwork?.id || null) || [selectedArtwork?.id || null],
      selectedArtworks: state.selectedArtworks?.concat(selectedArtwork) || [selectedArtwork],
    };

    artworkStates.push({
      ...defaultArtworkState,
      rotate: typeof selectedArtwork?.rotate === 'undefined' ? defaultArtworkState.rotate : selectedArtwork.rotate,
      imagePlacement: selectedArtwork?.placement === -1 ? null : (selectedArtwork.placement || defaultArtworkState.imagePlacement),
    });

    setUpscaleCheckbox(selectedArtwork, artworkContainer);

    if (!skipLoadCanvas) {
      loadCanvasPreview();
      addArtworkToTemplatesProperties();
    }
  };

  let addPattern = (selectedPattern, skipLoadCanvas) => {
    $('.js-action-addPattern').addClass('d-none');

    const artworkContainer = $('#filledPattern');
    artworkContainer.removeClass('d-none');

    setHeaderArtwork(selectedPattern, artworkContainer);

    state = {
      ...state,
      patternAssetId: selectedPattern?.id || null,
      selectedPattern,
    };

    if (!skipLoadCanvas) {
      loadCanvasPreview();
    }
  };

  let setHeaderArtwork = (selectedArtwork, artworkContainer) => {
    setHeaderArtworkImage(selectedArtwork, artworkContainer);
    setHeaderArtworkTitles(selectedArtwork, artworkContainer);
  };

  let setHeaderArtworkImage = (selectedArtwork, artworkContainer) => {
    const thumbnail = selectedArtwork.thumbnailImageUri;
    const image = getImageFromArtwork(selectedArtwork) || thumbnail;

    $('.form-filled__thumbnail', artworkContainer).empty()
      .html(`<a href="${image}" class="magnific-image table-thumbnail bg-chess"><div style="background-image:url('${thumbnail}')"></div></a>`);

    $('.magnific-image', artworkContainer).magnificPopup({ type: 'image' });
  };

  let getImageFromArtwork = (selectedArtwork) => {
    const files = getMainFileVersionFiles(selectedArtwork);

    return getImageByType($config.fileTypes.pngFromVector, files)?.uri ||
      getImageByType($config.fileTypes.pngHigh, files)?.uri;
  };

  let getImageByType = (fileType, files) => {
    return files.find(f => f.fileType === fileType);
  };

  let setHeaderArtworkTitles = (selectedArtwork, artworkContainer) => {
    $('.form-filled__name', artworkContainer).empty().html(selectedArtwork?.title);
  };

  let deleteArtworkOrPattern = (isPattern, event) => {
    if (isPattern) {
      $('.js-action-addPattern').removeClass('d-none');
      $('#filledPattern').addClass('d-none');

      state = {
        ...state,
        patternAssetId: null,
        selectedPattern: null,
      };

      setPatternParameters(defaultState);

      loadCanvasPreview();

      return;
    }

    clearArtworkParameters($('.filled-central-artwork').index(event.delegateTarget));
    loadCanvasPreview();

    event.delegateTarget.remove();
  };

  let clearArtworkParameters = (index) => {
    if (index === -1) {
      return;
    }

    state.imageAssetIds?.splice(index, 1);
    state.selectedArtworks?.splice(index, 1);
    artworkStates?.splice(index, 1);

    if (!state.selectedArtworks.length && !textStates?.length) {
      App.pageData('currenteditedcontent.templatesProperties', false);
      return;
    }

    let templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    Object.keys(templatesProperties).forEach((key) => {
      templatesProperties[key].imageProperties?.splice(index, 1);
    });

    App.pageData('currenteditedcontent.templatesProperties', templatesProperties);
  };

  let setArtworkParameters = ({ rotate, imagePlacement }, index) => {
    const artworkContainer = $($('.filled-central-artwork')[index]);

    $('.artwork-placement-control', artworkContainer).val(imagePlacement).trigger('change');
    $('.js-action-rotate [type="checkbox"]', artworkContainer).prop('checked', rotate);

    if (artworkStates[index]) {
      artworkStates[index] = {
        ...artworkStates[index],
        imagePlacement,
        rotate,
      };
    }
  };

  let setPrintArea = ({ printArea }) => {
    $('.artwork-print-area-control').val(printArea).trigger('change');

    state = {
      ...state,
      printArea,
    };
  };

  let setPatternParameters = ({ ratio, scalePatternToFill }) => {
    setTimeout(() => $('.js-blockScale-div')?.[0]?.noUiSlider?.set([ratio]));
    $('#filledPattern .js-action-upscale-to-fill [type="checkbox"]').prop('checked', scalePatternToFill);

    if (scalePatternToFill) {
      $('.js-blockScale-div')[0].setAttribute('disabled', 'disabled');
    } else {
      $('.js-blockScale-div')[0].removeAttribute('disabled');
    }

    state = { ...state, ratio, scalePatternToFill };
  };

  let loadCanvasPreview = (initLoad) => {
    if (!initLoad) {
      removeFiles();
    }

    $canvasXhr?.abort();
    UI.block('#complexArtworkCanvas');

    $canvasXhr = generateAsset(getDataForGenerateAsset(), (res) => {
      UI.unblock('#complexArtworkCanvas');

      if (res.status !== 200) {
        return;
      }

      App.pageData('currenteditedcontent.shortArtworks', getShortArtworks());
      App.pageData('currenteditedcontent.backgroundColor', state.backgroundColor);

      if (!initLoad) {
        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);
      }

      const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;

      const area = {
        x: state.template.previewAreas[printAreaIndex].x,
        y: state.template.previewAreas[printAreaIndex].y,
        w: state.template.previewAreas[printAreaIndex].width,
        h: state.template.previewAreas[printAreaIndex].height,
        previewHeight: state.template.previewHeight,
      };

      canvas.render({
        blankUri: res?.responseJSON?.file?.uri,
        templateUri: state.template?.thumbnailImagePath,
        area,
        arts: state.selectedArtworks?.map((sa, index) => ({
          uri: sa.thumbnailImageUri,
          ...getArtworkPositionForCanvas(state.template.previewAreas[printAreaIndex], res?.responseJSON?.rotate, index),
          actionId: ID(3)
        })),
        texts: [...textStates],
        artworkPositionChangedHandler,
        textPositionChangedHandler,
      });
    });
  };

  let getArtworkPositionForCanvas = (previewArea, rotateFromAssetApi, index) => {
    if (artworkStates[index]?.w !== null) {
      return { ...artworkStates[index] };
    }

    const previewHeight = state.template.previewHeight;
    const rotate = getArtworkDefaultRotateValue(rotateFromAssetApi);

    const mainFileVersionFiles = getMainFileVersionFiles(state.selectedArtworks[index]);
    const artworkFile = getImageByType($config.fileTypes.pngFromVector, mainFileVersionFiles) ||
      getImageByType($config.fileTypes.pngHigh, mainFileVersionFiles);

    const artWidth = Number(artworkFile?.additionalValues?.width);
    const artHeight = Number(artworkFile?.additionalValues?.height);

    // INFO rotate not moved artwork if rotate enabled for all templates
    const isRotatedByDefault = rotate === 90 || rotate === 270;
    const verticalSide = isRotatedByDefault ? artWidth : artHeight;
    const horizontalSide = isRotatedByDefault ? artHeight : artWidth;

    // INFO calculate multiplier for write artwork to area
    const artMultiplierW = previewArea.height / verticalSide;
    const artMultiplierH = previewArea.width / horizontalSide;
    const artMultiplier = Math.min(artMultiplierW, artMultiplierH);

    const params = {
      w: artWidth * artMultiplier,
      h: artHeight * artMultiplier,
    };

    const maxWidthHeight = {
      w: artWidth * previewHeight / state.template.height,
      h: artHeight * previewHeight / state.template.height,
    };

    // INFO recalculate w and h if artwork smaller than parent area
    // artwork on children templates should be the same size as on the parent template

    const parentTemplate = getParentTemplate();
    const currentArea = getCurrentTemplatePreviewArea();

    if (parentTemplate) {
      const parentFullAreaH = parentTemplate.height * currentArea.height / previewHeight;
      const parentFullAreaW = parentTemplate.width * currentArea.height / previewHeight;
      const ratio = state.template.height / parentTemplate.height;

      if (
        verticalSide < parentFullAreaH && horizontalSide < parentFullAreaW &&
        (params.w > maxWidthHeight.w * ratio || params.h > maxWidthHeight.h * ratio)
      ) {
        params.w = maxWidthHeight.w * ratio;
        params.h = maxWidthHeight.h * ratio;
      }
    }

    // INFO recalculate w and h if artwork smaller than area
    const fullAreaH = state.template.height * previewArea.height / previewHeight;
    const fullAreaW = state.template.width * previewArea.height / previewHeight;

    if (
      verticalSide < fullAreaH && horizontalSide < fullAreaW &&
      (params.w > maxWidthHeight.w || params.h > maxWidthHeight.h)
    ) {
      params.w = maxWidthHeight.w;
      params.h = maxWidthHeight.h;
    }

    return {
      ...params,
      ...getOffsetByPlacement({
        w: isRotatedByDefault ? params.h : params.w,
        h: isRotatedByDefault ? params.w : params.h,
      }, index),
      a: rotate,
      maxW: maxWidthHeight.w,
      maxH: maxWidthHeight.h,
    };
  };

  let artworkPositionChangedHandler = (art, index) => {
    artworkStates[index] = {
      ...artworkStates[index],
      x: art.x,
      y: art.y,
      w: art.w,
      h: art.h,
      a: art.a,
    };

    setArtworkParameters({ imagePlacement: null, rotate: artworkStates[index].rotate }, index);
    setPrintArea(state);

    removeFiles();
    storeStateToTemplate();
  };

  let getArtworkDefaultRotateValue = (rotateFromAssetApi) => {
    let rotate = rotateFromAssetApi || 0;

    if (state.template?.flip) {
      rotate += 180;
    }

    return rotate;
  };

  let getDataForGenerateAsset = () => {
    const body = {
      height: state.height,
      backgroudColor: state.backgroundColor,
      template: state.template,
      printArea: state.printArea,
      patternAssetId: state.patternAssetId,
      patternRatio: state.ratio,
      scalePatternToFill: state.scalePatternToFill,
      previewMode: true,
    };

    const isSomeArtworkPositionChanged = artworkStates?.some((as) => as?.w !== null && as?.w !== undefined);
    body.rotate = isSomeArtworkPositionChanged ? false : (artworkStates[0] ? artworkStates[0].rotate : defaultArtworkState.rotate);


    body.imageAreas = artworkStates?.map((as, index) => {
      const isArtworkPositionChanged = as?.w !== null && as?.w !== undefined;

      let rotateValue = as.a;
      let widthHeight = `${as?.w};${as?.h}`;

      if (!isArtworkPositionChanged) {
        const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;
        const params = getArtworkPositionForCanvas(state.template.previewAreas[printAreaIndex], 0, 0);

        widthHeight = `${parseInt(params.w)};${parseInt(params.h)}`;
      }

      const offset = `${as?.x};${as?.y}`;

      // INFO fix API bug: negative rotateValue is not working
      rotateValue = rotateValue >= 0 ? rotateValue : 360 + rotateValue;

      return ({
        rotateValue: isArtworkPositionChanged ? rotateValue : 0,
        imagePlacement: isArtworkPositionChanged ? 'TopLeft' : as?.imagePlacement || 'TopLeft',
        id: state.imageAssetIds[index],
        offset: isArtworkPositionChanged ? offset : '0;0',
        previewHeight: state.template.previewHeight,
        widthHeight,
      });
    }) || [];

    body.textAreas = textStates.map((ts) => ({
      fontSize: ts.fontSize,
      text: ts.text,
      offset: `${ts.x};${ts.y}`,
      widthHeight: `${ts.w};${ts.h}`,
      previewHeight: state.template.previewHeight,
      fontColor: ts.fontColor,
      fontFamily: ts.fontFamily,
      isPersonalization: ts.isPersonalization,
      gravity: ts.gravity,
    }));

    return body;
  };

  let generateAsset = (data, completeCallback) => {
    const body = { ...data };
    if (body.previewMode) {
      delete body.previewMode;
      delete body.printArea;
      delete body.textAreas;
      body.imageAreas = body.imageAreas.map((a) => a.id);
    }

    let key = JSON.stringify(body);

    if (responseMemo[key] && responseMemo[key].statusText === 'OK') {
      return completeCallback(responseMemo[key]);
    }

    if (artworkStates?.some((as) => as?.w !== null)) {
      // check if rotate field should be ignored
      // rotate value checked in previous condition, for ignore, need to check inverted value
      body.rotate = !body.rotate;
    }

    key = JSON.stringify(body);

    if (responseMemo[key] && responseMemo[key].statusText === 'OK') {
      return completeCallback(responseMemo[key]);
    }

    return $.ajax({
      type: 'post',
      url: `${$config.apiURL}File/transform/asset`,
      data: JSON.stringify(data),
      dataType: 'json',
      contentType: 'application/json',
      crossDomain: true,
      async: true,
      headers: App.ajaxHeaders(),
      complete: (res) => {
        responseMemo[key] = res;
        completeCallback(res);
      }
    });
  };

  let placementChangedHandler = (e) => {
    const index = $('.filled-central-artwork').index(e.delegateTarget.closest('.filled-central-artwork'));

    if (index === -1 || !artworkStates[index]) {
      return;
    }

    artworkStates[index] = {
      ...artworkStates[index],
      imagePlacement: $(e.target).val(),
    };

    artworkStates[index] = {
      ...artworkStates[index],
      ...getOffsetByPlacement(null, index),
    };

    loadCanvasPreview();
    storeStateToTemplate();
    setArtworkParameters(artworkStates[index] || {}, index);
    setPrintArea(state);
  };

  let templateChangedHandler = (e) => {
    let selectedTemplateName = $(e.target).val();
    let parentTemplateName = $(e.target).find('option')
      .filter((i, el) => $(el).text().trim() === selectedTemplateName).data('parent');

    const defaults = App.pageData('currenteditedcontent.defaults');

    setTemplate(
      !parentTemplateName ?
        defaults?.AopTemplates?.find(t => t.templateName === selectedTemplateName) :
        defaults?.AopTemplates?.find(t => t.templateName === parentTemplateName)?.details
          ?.find(t => t.templateName === selectedTemplateName),
      parentTemplateName
    );

    artworkStates?.forEach((as, index) => setArtworkParameters(as, index));
    setPrintArea(state);

    canvas.destroy();

    loadCanvasPreview(true);
  };

  let printAreaChangedHandler = (e) => {
    state = { ...state, printArea: $(e.target).val() };

    artworkStates = artworkStates.map(() => ({
      ...defaultArtworkState,
      a: getArtworkDefaultRotateValue(),
    })) || [];

    textStates = textStates?.map((ts) => ({
      ...ts,
      fontSize: getMaxFontSize({
        fontSize: defaultTextState.fontSize,
        fontFamily: ts.fontFamily || defaultTextState.fontFamily,
        text: ts.text || defaultText,
      }) || defaultTextState.fontSize,
      x: defaultTextState.x,
      y: defaultTextState.y,
      w: defaultTextState.w,
      h: defaultTextState.h,
      moved: false,
    })) || [];

    artworkStates?.forEach((as, index) => setArtworkParameters(as, index));
    setPrintArea(state);
    loadCanvasPreview();

    if (textStates?.[0]?.text) {
      return;
    }

    // info storeStateToTemplate will be called on text position recalculate (textPositionChangedHandler)
    storeStateToTemplate();
  };

  let colorChangedHandler = (e) => {
    state = { ...state, backgroundColor: $(e.target).val() };
    loadCanvasPreview();
  };

  let rotateChangedHandler = (e) => {
    $('.filled-central-artwork .js-action-rotate [type="checkbox"]').prop('checked', e.target.checked);

    artworkStates = artworkStates.map((as) => ({
      ...as,
      rotate: e.target.checked,
    }));

    loadCanvasPreview();
  };

  let upscaleChangedHandler = (e) => {
    const index = $('.filled-central-artwork').index(e.delegateTarget.closest('.filled-central-artwork'));

    if (!e.target.checked) {
      return;
    }

    $(e.target).prop('disabled', true);

    generatePngFromVector(state.selectedArtworks[index]);
  };

  let personalizationChangedHandler = (e) => {
    const index = $('.filled-text').index(e.delegateTarget);
    const value = e.target.checked;

    if (index === -1 || !textStates[index]) {
      return;
    }

    textStates[index] = { ...textStates[index], isPersonalization: value };

    if (value) {
      $('.js-action-personalization [type="checkbox"]').prop('checked', false);
      $('.js-action-gravity [type="checkbox"]').prop('checked', false);
      $('.gravity-row').addClass('d-none');

      $('.js-action-personalization [type="checkbox"]', $(e.delegateTarget)).prop('checked', true);
      $('.gravity-row', $(e.delegateTarget)).removeClass('d-none');

      textStates = textStates
        .map((ts, i) => i === index ?
          { ...ts, isPersonalization: true, gravity: GRAVITY.LEFT } :
          { ...ts, isPersonalization: false, gravity: GRAVITY.LEFT }
        );
    } else {
      $('.gravity-row', $(e.delegateTarget)).addClass('d-none');
      textStates[index] = { ...textStates[index], gravity: GRAVITY.LEFT };
    }

    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let gravityChangedHandler = (e) => {
    const index = $('.filled-text').index(e.delegateTarget);
    const value = e.target.checked;

    if (index === -1 || !textStates[index]) {
      return;
    }

    textStates[index] = { ...textStates[index], gravity: value ? GRAVITY.CENTER : GRAVITY.LEFT };

    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let upscaleToFillChangedHandler = (e) => {
    const value = e.target.checked;

    state = { ...state, scalePatternToFill: value };

    if (value) {
      $('.js-blockScale-div')[0].setAttribute('disabled', 'disabled');
    } else {
      $('.js-blockScale-div')[0].removeAttribute('disabled');
    }

    const selectedArtworkFiles = getMainFileVersionFiles(state.selectedPattern);
    const hasGeneratedVectorFile = selectedArtworkFiles.some(f => f.fileType === $config.fileTypes.pngFromVector);

    if (isSourceFileIsVector(state.selectedPattern) || hasGeneratedVectorFile) {
      loadCanvasPreview();
      return;
    }

    upscalePngForPattern(state.selectedPattern);
  };

  let ratioChangedHandler = (value) => {
    state = { ...state, ratio: value };
    loadCanvasPreview();
  };

  let removeFiles = () => {
    App.pageData('currenteditedcontent.versionFilesStringified', '[]');
  };

  let generateFiles = (uri, template) => {
    if (!uri || !template) {
      removeFiles();

      return;
    }

    const matches = uri.match(/FileName=([^&]*)/);
    const fileName = matches?.[1];

    if (!fileName) {
      removeFiles();

      return;
    }

    const versionFiles = [{
      files: [{
        partType: 3,
        fileName: fileName,
        fileStorageName: fileName,
        folderName: 'artworks',
        containerName: User.accountID(),
        uri,
        fileType: 1,
        additionalValues: {
          width: template.width, height: template.height, srcFile: true
        },
        thumbnailImageUri: null,
        IsAdd: true
      }, {
        partType: 3,
        fileName: fileName,
        fileStorageName: fileName,
        folderName: 'artworks',
        containerName: User.accountID(),
        uri,
        fileType: 0,
        additionalValues: {
          autoGenerated: 'true', width: template.width, height: template.height,
        },
        thumbnailImageUri: null,
        IsAdd: true
      }], name: 'All', colorGroup: 'All', thumbnailImageUri: uri, systemGenerated: false
    },];

    App.pageData('currenteditedcontent.versionFilesStringified', JSON.stringify(versionFiles));
  };

  let getShortArtworks = () => {
    let selectedArtworks = state.selectedArtworks?.map((sa, index) => {
      let selectedArtwork = mapping.prepareShortArtwork(sa);

      selectedArtwork = selectedArtwork && {
        ...selectedArtwork,
        rotate: artworkStates[index]?.rotate,
        placement: artworkStates[index].imagePlacement || '-1',
      };

      return selectedArtwork;
    }) || null;


    let selectedPattern = mapping.prepareShortArtwork(state.selectedPattern);
    selectedPattern = selectedPattern && {
      ...selectedPattern,
      ratio: state.ratio || defaultState.ratio
    };

    return [selectedPattern, ...selectedArtworks].filter(Boolean);
  };

  let isValid = () => {
    const savedVersionFiles = JSON.parse(App.pageData('currenteditedcontent.versionFilesStringified') || '[]');
    const shortArtworks = App.pageData('currenteditedcontent.shortArtworks');

    if ((!savedVersionFiles?.length && !$isDtg) || !shortArtworks?.length) {
      missingVersionFileDialog();
      UI.scrollToElement('.complex-artwork-file');

      return false;
    }

    return true;
  };

  let generateFirstTemplateAsset = () => {
    if (!isCanvasChanged()) {
      return null;
    }

    if ($isDtg) {
      App.pageData('currenteditedcontent.versionFilesStringified', '[]');
      return null;
    }

    const savedVersionFiles = JSON.parse(App.pageData('currenteditedcontent.versionFilesStringified') || '[]');
    if (savedVersionFiles?.length) {
      return null;
    }

    const defaults = App.pageData('currenteditedcontent.defaults');

    const { template, parentTemplateName } = state;
    setTemplate(defaults?.AopTemplates?.[0]);

    const data = {
      ...getDataForGenerateAsset(),
      template: defaults?.AopTemplates?.[0],
      previewMode: false,
    };

    setTemplate(template, parentTemplateName);

    return new Promise((resolve, reject) => {
      generateAsset(data, (res) => {

        if (res.status !== 200) {
          removeFiles();
          reject();

          return;
        }

        generateFiles(res?.responseJSON?.file?.uri, state.template);
        resolve();
      });
    });
  };

  let missingVersionFileDialog = () => {
    bootbox.dialog({
      title: '<i class="fal fa-exclamation-triangle text-danger mr-2"></i> ' + $lang.artwork.Warning,
      message: 'Missing Complex Artwork - Design',
      buttons: {
        Ok: {
          label: $lang.btn.ok, className: 'btn btn-danger',
        }
      }
    });
  };

  let fillFormByArtworkData = (artwork) => {
    const currentArtwork = App.pageData('currenteditedcontent');

    Object.assign(currentArtwork, {
      accountId: artwork.accountId,
      title: artwork.title,
      version: artwork.version,
      designer: artwork.designer,
      licensor: artwork.licensor,
      licensee: artwork.licensee,
      brand: artwork.brand,
      subbrand: artwork.subbrand,
      tags: artwork.tags,
      collections: artwork.collections,
      containsLegalTagLine: artwork.containsLegalTagLine,
      bulletPoints: artwork.bulletPoints,
      description: artwork.description,
    });

    const data = {
      $lang,
      artwork: currentArtwork,
      artworkType: artworkType.complex,
      isLicensorSelectDisabled: User.isLicensor() || !!currentArtwork.id || !currentArtwork.accountId
    };

    App.pageDataInit(data);

    let template = require(`./tmpl/partial/description.hbs`);
    $('#descriptionContainer').empty().html(template(data));

    dataBinder.dataBind(document.body, window);
    App.pageData('currenteditedcontent', currentArtwork);

    // listeners
    $callbacks.portletDescriptionInit();
  };

  let setArtworkOnInit = (artwork) => {
    if (!artwork.id) {
      return;
    }

    const selectedArtworks = Object.values(artwork?.shortArtworks || {}).filter(a => a.artworkType === 'Central') || [];
    const selectedPattern = Object.values(artwork?.shortArtworks || {}).find(a => a.artworkType === 'Pattern') || null;

    state = {
      ...state,
      imageAssetIds: [],
      patternAssetId: selectedPattern?.id || null,
      backgroundColor: artwork.backgroundColor || defaultState.backgroundColor,
      selectedPattern,
      ratio: selectedPattern?.ratio || defaultState.ratio,
      scalePatternToFill: artwork.scalePatternToFill || defaultState.scalePatternToFill,
    };

    if (selectedArtworks) {
      selectedArtworks.forEach((sa) => {
        sa.thumbnailImageUri =
          getImageByType($config.fileTypes.pngLow, getMainFileVersionFiles(sa))?.uri;

        addArtwork(sa, true);
      });
    }

    if (selectedPattern) {
      selectedPattern.thumbnailImageUri = getImageFromArtwork(selectedPattern);
      addPattern(selectedPattern, true);
    }

    $('.complex-artwork-file [name="artworkBackgroundColor"]').val(state.backgroundColor);
    document.querySelector('.complex-artwork-file [name="artworkBackgroundColor"]').dispatchEvent(new Event('input', { bubbles: true }));

    artworkStates?.forEach((as, index) => setArtworkParameters(as, index));
    setPrintArea(state);
    setPatternParameters(state);
  };

  let storeStateToTemplate = (skipRemovingChildrenProps = false) => {
    let templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    if (!skipRemovingChildrenProps) {
      state.template?.details?.forEach((t) => delete templatesProperties[t.templateName]);
    }

    // artwork state
    templatesProperties[state.template?.templateName] = {
      printArea: state.printArea,
      imageProperties: artworkStates.map((as) => ({
        placement: as.imagePlacement || '-1',
        widthHeight: !!as?.w && !!as?.h ? `${as?.w};${as?.h}` : null,
        offset: !as?.imagePlacement ? `${as?.x};${as?.y}` : '0;0',
        rotateValue: fixRotateValueByFlip(as?.a, state.template?.templateName),
      })),
    };

    // text state
    templatesProperties = storeTextStateToTemplate(templatesProperties);

    App.pageData('currenteditedcontent.templatesProperties', templatesProperties);
  };

  let restoreStateToTemplate = (templateName = state.template?.templateName) => {
    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    const properties = templatesProperties[templateName];

    if (!properties) {
      setArtworkStateFromParentTemplate();
      return;
    }

    state = {
      ...state,
      printArea: properties.printArea || defaultState.printArea,
    };

    properties.imageProperties?.forEach((ap, index) => {
      const { x, y, w, h } = getOffsetSize(ap.offset, ap.widthHeight, defaultArtworkState);
      const a = ap.rotateValue || defaultArtworkState.a;

      artworkStates[index] = {
        ...artworkStates[index],
        x,
        y,
        w,
        h,
        a: fixRotateValueByFlip(a, templateName),
        imagePlacement: ap.placement === '-1' ? null : (ap.placement || defaultArtworkState.imagePlacement),
      };

      if (ap.placement !== '-1') {
        const rad = artworkStates?.[index]?.a * Math.PI / 180;
        const boxWidth = Math.ceil(artworkStates?.[index]?.w * Math.cos(rad) + artworkStates?.[index]?.h * Math.sin(rad));
        const boxHeight = Math.ceil(artworkStates?.[index]?.h * Math.cos(rad) + artworkStates?.[index]?.w * Math.sin(rad));

        artworkStates[index] = {
          ...artworkStates[index],
          ...getOffsetByPlacement({ w: boxWidth, h: boxHeight }, index),
        };
      }
    });

    // text
    restoreTextAreaForTemplate(templateName);
  };

  let setTemplate = (template, parentTemplateName) => {
    state = { ...state, template: template || null, parentTemplateName: parentTemplateName || null };
    restoreStateToTemplate();

    $('.filled-text').each((index, el) => {
      $('[name="fontSize"]', $(el)).val(textStates?.[index]?.fontSize || defaultTextState.fontSize);
    });
  };

  let getMainFileVersionFiles = (selectedArtwork) => {
    return selectedArtwork?.versionFiles
      ?.sort((a, b) => {
        if (Number.isInteger(a.colorGroup)) {
          return a.colorGroup < b.colorGroup ? -1 : 1;
        }

        return colorGroups[a.colorGroup] < colorGroups[b.colorGroup] ? -1 : 1;
      })
      ?.[0]?.files || [];
  };

  let getSourceFile = (files) => {
    return files.find(f => f.additionalValues?.srcFile === true || f.additionalValues?.srcFile === 'True');
  };

  let isSourceFileIsVector = (selectedArtwork) => {
    return getSourceFile(getMainFileVersionFiles(selectedArtwork))?.fileType === $config.fileTypes.aiPsd12;
  };

  let isVectorImageQuestion = (selectedArtwork) => {
    const template = require('./tmpl/dialog/is-vector-prompt.hbs');

    bootbox.dialog({
      title: $lang.assetsManager.artwork.VectorFile,
      message: template({ $lang }),
      size: 'large',
      buttons: {
        ok: {
          label: $lang.btn.ok,
          className: 'btn btn-primary',
          callback: () => {
            let isVectorInput = $('input[name="isVector"]:checked');

            const isVector = isVectorInput.length && isVectorInput.val() === 'yes';

            const selectedArtworkFiles = getMainFileVersionFiles(selectedArtwork);
            const hasGeneratedVectorFile = selectedArtworkFiles.some(f => f.fileType === $config.fileTypes.pngFromVector);

            if (!isVector || hasGeneratedVectorFile) {
              addArtwork(selectedArtwork, false, true);
              return;
            }

            generatePngFromVector(selectedArtwork, true);
          }
        }
      }
    });
  };

  let generatePngFromVector = (selectedArtwork, isPngFromVectorAwaiting = false) => {
    UI.block('.js-action-addArtwork, .filled-central-artwork');

    $isOpenModalDisabled = true;

    $artworkAwaitingGenerating = selectedArtwork;
    $isPngFromVectorAwaiting = isPngFromVectorAwaiting;

    const srcFile = getSourceFile(getMainFileVersionFiles(selectedArtwork));

    if (!srcFile) {
      return;
    }

    const data = {
      accountId: selectedArtwork.accountId,
      fileUri: srcFile.uri,
      fileType: srcFile.fileType,
      filesToCreate: [$config.fileTypes.pngFromVector, $config.fileTypes.thumbnailPngFromVector],
      fileName: srcFile.fileName,
      isTemp: false,
      processingMode: 'Background',
      objectConnector: {
        id: selectedArtwork.id,
        applicationModule: 'Artwork'
      }
    };

    $.when(App.ajaxData('File/createFiles', data, 'POST')).done((res) => {
      if (res.errorInformation) {
        UI.unblock('.js-action-addArtwork, .filled-central-artwork');
        $isOpenModalDisabled = false;
      }
    });
  };

  let upscalePngForPattern = (selectedPattern) => {
    UI.block('#filledPattern');

    $isOpenModalDisabled = true;

    $patternAwaitingGenerating = selectedPattern;

    const srcFile = getSourceFile(getMainFileVersionFiles(selectedPattern));

    if (!srcFile) {
      return;
    }

    const data = {
      accountId: selectedPattern.accountId,
      fileUri: srcFile.uri,
      fileType: srcFile.fileType,
      filesToCreate: [$config.fileTypes.pngFromVector, $config.fileTypes.thumbnailPngFromVector],
      fileName: srcFile.fileName,
      isTemp: false,
      processingMode: 'Background',
      objectConnector: {
        id: selectedPattern.id,
        applicationModule: 'Artwork'
      }
    };

    $.when(App.ajaxData('File/createFiles', data, 'POST')).done((res) => {
      if (res.errorInformation) {
        UI.unblock('#filledPattern');
        $isOpenModalDisabled = false;
      }
    });
  };

  let fileGeneratorNotificationHandler = (e) => {
    const selectedAssets = [
      $artworkAwaitingGenerating,
      ...state.selectedArtworks,
      $patternAwaitingGenerating || state.selectedPattern || {},
    ];

    const message = e.detail?.notificationMessage;

    const affectedAsset = selectedAssets.find(asset => message?.ObjectId === asset?.id);

    if (!affectedAsset) {
      return;
    }

    if (affectedAsset.artworkType === 'Central') {
      artworkAwaitingGeneratingHandler(message, affectedAsset);
      return;
    }

    patternAwaitingGeneratingHandler(message, affectedAsset);
  };

  let artworkAwaitingGeneratingHandler = (message, selectedArtwork) => {
    const isUpscale = !$isPngFromVectorAwaiting;

    if (!isUpscale && $artworkAwaitingGenerating?.id !== message?.ObjectId) {
      return;
    }

    if ($artworkAwaitingGenerating?.id === message?.ObjectId) {
      $artworkAwaitingGenerating = null;

      UI.unblock('.js-action-addArtwork, .filled-central-artwork');
      $isOpenModalDisabled = false;
    }

    if (!Array.isArray(message.AdditionalData) && !isUpscale) {
      return;
    }

    // Error
    if (message.AdditionalData?.State === 1) {
      state.selectedArtworks.forEach((sa, index) => {
        if (sa.id !== selectedArtwork.id) {
          return;
        }

        setUpscaleCheckbox(sa, $($('.filled-central-artwork')[index]));
      });

      return;
    }

    state.selectedArtworks.forEach((sa, index) => {
      if (sa.id !== selectedArtwork.id) {
        return;
      }

      setPngFilesToSelectedArtwork(sa, message.AdditionalData);
      setUpscaleCheckbox(sa, $($('.filled-central-artwork')[index]));

      artworkStates[index] = {
        ...defaultArtworkState,
        rotate: artworkStates[index].rotate,
      };

      setArtworkParameters(artworkStates[index] || {}, index);
    });

    if (!isUpscale) {
      setPngFilesToSelectedArtwork(selectedArtwork, message.AdditionalData);
      addArtwork(selectedArtwork, false, true);
      return;
    }

    loadCanvasPreview();
    storeStateToTemplate();
  };

  let setPngFilesToSelectedArtwork = (selectedArtwork, additionalData) => {
    const files = getMainFileVersionFiles(selectedArtwork);

    additionalData.forEach((data) => {
      files.push({
        partType: 3,
        additionalValues: data.AdditionalValues,
        fileName: data.FileName,
        fileStorageName: data.FileStorageName,
        uri: data.Uri,
        containerName: data.ContainerName,
        folderName: data.FolderName,
        fileType: data.FileType,
        acceptedFile: '.png',
        uploadMultiple: false,
      });
    });
  };

  let patternAwaitingGeneratingHandler = (message, selectedPattern) => {
    $patternAwaitingGenerating = null;

    UI.unblock('#filledPattern');
    $isOpenModalDisabled = false;

    if (!Array.isArray(message.AdditionalData)) {
      return;
    }

    if (message.AdditionalData?.State === 1) {
      $('#filledPattern .js-action-upscale-to-fill [type="checkbox"]').prop('checked', false);

      return;
    }

    const files = getMainFileVersionFiles(selectedPattern);

    message.AdditionalData.forEach((data) => {
      files.push({
        partType: 3,
        additionalValues: data.AdditionalValues,
        fileName: data.FileName,
        fileStorageName: data.FileStorageName,
        uri: data.Uri,
        containerName: data.ContainerName,
        folderName: data.FolderName,
        fileType: data.FileType,
        acceptedFile: '.png',
        uploadMultiple: false,
      });
    });

    state = { ...state, patternAssetId: selectedPattern?.id || null };
    loadCanvasPreview();
  };

  let setUpscaleCheckbox = (selectedArtwork, artworkContainer) => {
    const files = getMainFileVersionFiles(selectedArtwork);
    const srcFile = getSourceFile(files);

    if (srcFile?.fileType !== $config.fileTypes.pngHigh) {
      return;
    }

    $('.upscale-row', artworkContainer).removeClass('d-none');

    const hasPngFromVectorGeneratedFile = files.some(f => f.fileType === $config.fileTypes.pngFromVector);

    if (hasPngFromVectorGeneratedFile) {
      $('.js-action-upscale [type="checkbox"]', artworkContainer)
        .prop('checked', true)
        .prop('disabled', true);
    }
  };

  let getOffsetByPlacement = (box, index) => {
    box = box || canvas.getArtworkBBox(index);

    const placement = artworkStates[index]?.imagePlacement || defaultArtworkState.imagePlacement;

    const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;
    const area = state.template.previewAreas[printAreaIndex];

    const bottom = area.height - box.h;
    const centerY = Math.round((bottom) / 2);

    const right = area.width - box.w;
    const centerX = Math.round((right) / 2);

    switch (placement) {
      case 'Center':
        return { x: centerX, y: centerY };
      case 'MeddleLeft':
        return { x: 0, y: centerY };
      case 'MeddleRight':
        return { x: right, y: centerY };
      case 'TopCenter':
        return { x: centerX, y: 0 };
      case 'TopLeft':
        return { x: 0, y: 0 };
      case 'TopRight':
        return { x: right, y: 0 };
      case 'BottomCenter':
        return { x: centerX, y: bottom };
      case 'BottomLeft':
        return { x: 0, y: bottom };
      case 'BottomRight':
        return { x: right, y: bottom };
      default:
        return { x: centerX, y: centerY };
    }
  };

  let setAopTemplatesSelector = () => {
    const defaults = App.pageData('currenteditedcontent.defaults');

    App.pageData('currenteditedcontent.AopTemplates', (defaults?.AopTemplates || []).reduce((acc, t) => {
      acc.push({ templateName: t.templateName, parent: null });

      (t.details || []).forEach(d => acc.push({ templateName: d.templateName, parent: t.templateName }));

      return acc;
    }, []));
  };

  let templateResult = (opt) => {
    const text = $(opt.element).text();
    const parent = $(opt.element).attr('data-parent');

    return $(`<div class="select2-version-option ${parent ? 'ml-3' : 'bold'}">${text}</div>`);
  };

  let setArtworkStateFromParentTemplate = () => {
    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    if (!state.parentTemplateName || !templatesProperties[state.parentTemplateName]) {
      artworkStates = artworkStates?.map((as) => ({ ...defaultArtworkState, rotate: as.rotate })) || [];
      state = { ...state, printArea: defaultState.printArea };
      restoreMainTextArea();

      return;
    }

    restoreStateToTemplate(state.parentTemplateName);

    const currentArea = getCurrentTemplatePreviewArea();
    const parentArea = getParentTemplatePreviewArea();

    const widthRatio = currentArea.width / parentArea.width;
    const heightRatio = currentArea.height / parentArea.height;

    const ratio = Math.min(widthRatio, heightRatio);

    artworkStates = artworkStates?.map((as) => ({
      x: Math.round(as.x * ratio),
      y: Math.round(as.y * ratio),
      w: as.w === null ? null : Math.round(as.w * ratio),
      h: as.h === null ? null : Math.round(as.h * ratio),
      a: as.a,
    })) || [];
  };

  let getParentTemplate = () => {
    const defaults = App.pageData('currenteditedcontent.defaults');
    return defaults?.AopTemplates?.find(t => t.templateName === state.parentTemplateName);
  };

  let getTemplateByName = (templateName) => {
    const defaults = App.pageData('currenteditedcontent.defaults');
    return defaults?.AopTemplates?.find(t => t.templateName === templateName);
  };

  let fixRotateValueByFlip = (rotateValue, templateName) => {
    const template = getTemplateByName(templateName);

    if (!template?.flip) {
      return rotateValue >= 0 ? rotateValue : 360 + rotateValue;
    }

    rotateValue -= 180;

    return rotateValue >= 0 ? rotateValue : 360 + rotateValue;
  };

  let getParentTemplatePreviewArea = () => {
    const parentTemplate = getParentTemplate();
    const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;

    return parentTemplate?.previewAreas[printAreaIndex];
  };

  let getCurrentTemplatePreviewArea = () => {
    const currentTemplate = state.template;
    const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;

    return currentTemplate.previewAreas[printAreaIndex];
  };

  let getTemplatePreviewArea = (templateName) => {
    const defaults = App.pageData('currenteditedcontent.defaults');

    let template;

    defaults?.AopTemplates?.some((t) => {
      if (t.templateName === templateName) {
        template = t;
        return true;
      }

      return (t.details || []).some((d) => {
        if (d.templateName !== templateName) {
          return false;
        }

        template = d;
        return true;
      });
    });

    if (!template) {
      return null;
    }

    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};
    const templatePrintArea = templatesProperties[templateName]?.printArea || defaultState.printArea;

    const printAreaIndex = templatePrintArea === printArea[0].value ? 0 : 1;
    return template.previewAreas[printAreaIndex];
  };

  let isCanvasChanged = () => {
    const savedVersionFiles = JSON.parse(App.pageData('currenteditedcontent.versionFilesStringified') || '[]');

    // INFO files are removed on canvas changed
    return !savedVersionFiles?.length;
  };

  // Text
  let addText = () => {
    const textContainer = $($('#filledText').html());
    textContainer.insertBefore($('.js-action-addText'));

    initHandlersForText(textContainer);

    textStates ||= [];
    textStates.push({
      ...defaultTextState,
      text: defaultText,
      gravity: GRAVITY.LEFT,
      isPersonalization: !$('#artworkDialogForm').serializeObject().isPersonalization,
    });

    addTextToTemplatesProperties();

    let textState = textStates[textStates.length - 1];
    textStates[textStates.length - 1] = {
      ...textState,
      ...getTextOffsetSize({ fontSize: defaultTextState.fontSize }, textState),
    };

    textState = textStates[textStates.length - 1];

    $('.artwork-font-control', textContainer).val(textState.fontFamily).trigger('change');
    $('[name="fontSize"]', textContainer).val(textState.fontSize);
    $('[name="text"]', textContainer).val(textState.text).focus();

    $('[name="fontColor"]', textContainer).val(textState.fontColor);
    $('[name="fontColor"]', textContainer)[0].dispatchEvent(new Event('input', { bubbles: true }));

    $('.js-action-personalization [type="checkbox"]', textContainer)
      .prop('checked', textState.isPersonalization);

    if (textState.isPersonalization) {
      $('.js-action-gravity [type="checkbox"]', textContainer)
        .prop('checked', textState.gravity === GRAVITY.CENTER);

      $('.gravity-row', textContainer).removeClass('d-none');
    }

    loadCanvasPreview();
  };

  let deleteText = (event) => {
    const index = $('.filled-text').index(event.delegateTarget);

    if (index === -1) {
      return;
    }

    textStates.splice(index, 1);
    event.delegateTarget.remove();

    if (!state.selectedArtworks.length && !textStates?.length) {
      App.pageData('currenteditedcontent.templatesProperties', false);
      loadCanvasPreview();
      storeStateToTemplate(true);
      return;
    }

    let templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    Object.keys(templatesProperties).forEach((key) => {
      templatesProperties[key].textProperties?.splice(index, 1);
    });

    App.pageData('currenteditedcontent.templatesProperties', templatesProperties);

    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let fontChangedHandler = async (e) => {
    const index = $('.filled-text').index(e.delegateTarget.closest('.filled-text'));

    if (index === -1 || !textStates[index]) {
      return;
    }

    textStates[index] = { ...textStates[index], fontFamily: $(e.target).val() };

    await loadFontForTextState(textStates[index]);

    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let fontColorChangedHandler = (e) => {
    const index = $('.filled-text').index(e.delegateTarget);

    if (index === -1 || !textStates[index]) {
      return;
    }

    textStates[index] = { ...textStates[index], fontColor: $(e.target).val() };
    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let fontSizeChangedHandler = (e) => {
    const index = $('.filled-text').index(e.delegateTarget);

    if (index === -1 || !textStates[index]) {
      return;
    }

    textStates[index] = { ...textStates[index], fontSize: parseInt($(e.target).val()) };
    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let textChangedHandler = (e) => {
    const index = $('.filled-text').index(e.delegateTarget);

    if (index === -1 || !textStates[index]) {
      return;
    }

    textStates[index] = { ...textStates[index], text: $(e.target).val() };
    loadCanvasPreview();
    storeStateToTemplate(true);
  };

  let textPositionChangedHandler = (textPosition, index) => {
    if (index === -1 || !textStates[index]) {
      return;
    }

    $($('.filled-text [name="fontSize"]')[index]).val(textPosition.fontSize);

    textStates[index] = {
      ...textStates[index],
      fontSize: textPosition.fontSize,
      x: textPosition.x,
      y: textPosition.y,
      w: textPosition.w,
      h: textPosition.h,
      moved: true,
    };

    storeStateToTemplate(!textPosition.moved);
    removeFiles();
  };

  let storeTextStateToTemplate = (templatesProperties) => {
    const textAreas = getTextAreas();

    if (!textAreas?.length) {
      App.pageData('currenteditedcontent.textAreas', false);

      Object.values(templatesProperties).forEach(val => delete val.textProperties);
      return templatesProperties;
    }

    templatesProperties[state.template?.templateName] = {
      ...templatesProperties[state.template?.templateName],
      textProperties: (textAreas || []).map((ta) => ({
        fontSize: ta.fontSize,
        offset: ta.offset,
        widthHeight: ta.widthHeight,
        previewHeight: ta.previewHeight,
      })),
    };

    App.pageData('currenteditedcontent.textAreas', textAreas);

    return templatesProperties;
  };

  let getTextAreas = () => {
    return textStates.reduce((acc, ts, index) => {
      if (!ts.text || !ts.w || !ts.h) {
        return acc;
      }

      acc.push({
        fontSize: ts.fontSize,
        text: ts.text,
        offset: `${ts.x};${ts.y}`,
        widthHeight: `${ts.w};${ts.h}`,
        fontFamily: ts.fontFamily,
        previewHeight: state.template.previewHeight,
        fontColor: ts.fontColor,
        isPersonalization: ts.isPersonalization,
        gravity: ts.gravity,
      });

      return acc;
    }, []);
  };

  let getOffsetSize = (offset, widthHeight, defaultState) => {
    const [x, y] = offset ? offset.split(';') : [defaultState.x, defaultState.y];
    const [w, h] = widthHeight ? widthHeight.split(';') : [defaultState.w, defaultState.h];

    return {
      x: Number(x),
      y: Number(y),
      w: w === null ? null : Number(w),
      h: h === null ? null : Number(h),
    };
  };

  let getTextOffsetSize = (textProperty, textState, templateName = state.template?.templateName) => {
    const maxFontSize = getMaxFontSize({
      fontSize: textProperty.fontSize,
      fontFamily: textState.fontFamily,
      text: textState.text,
    });

    const fontSize = maxFontSize < textProperty.fontSize ? maxFontSize : textProperty.fontSize;

    if (textProperty.offset && textProperty.widthHeight && maxFontSize >= textProperty.fontSize) {
      return {
        ...textState,
        fontSize: fontSize,
        ...getOffsetSize(textProperty.offset, textProperty.widthHeight, defaultTextState),
      };
    }

    const { w, h } = getTextWidthHeight({
      ...textState,
      fontSize,
    });

    const printArea = getTemplatePreviewArea(templateName) || { width: w, height: h };

    return {
      ...textState,
      fontSize,
      x: Math.round((printArea.width - w) / 2),
      y: Math.round((printArea.height - h) / 2),
      w,
      h,
    };
  };

  let setTextOnInit = (artwork) => {
    if (!artwork.id) {
      return;
    }

    const textAreas = artwork?.textAreas || [];
    if (!textAreas?.length) {
      return;
    }

    textStates = textAreas.map((ta) => ({
      ...defaultTextState,
      text: ta.text,
      fontFamily: ta.fontFamily,
      fontColor: ta.fontColor,
      isPersonalization: ta.isPersonalization,
      gravity: ta.gravity,
    }));

    textStates.forEach((ts) => {
      loadFontForTextState(ts);
    });

    restoreTextAreaForTemplate();

    textStates.forEach((ts) => {
      if (ts.w !== null) {
        ts = { ...ts, moved: true };
      }

      if (!ts.text) {
        return;
      }

      const textContainer = $($('#filledText').html());
      textContainer.insertBefore($('.js-action-addText'));

      initHandlersForText(textContainer);

      $('[name="fontSize"]', textContainer).val(ts.fontSize);
      $('[name="text"]', textContainer).val(ts.text);
      setTimeout(() => $('.artwork-font-control', textContainer).val(ts.fontFamily).trigger('change'));

      $('[name="fontColor"]', textContainer).val(ts.fontColor);
      $('[name="fontColor"]', textContainer)[0].dispatchEvent(new Event('input', { bubbles: true }));

      $('.js-action-personalization [type="checkbox"]', textContainer)
        .prop('checked', ts.isPersonalization);

      if (ts.isPersonalization) {
        $('.js-action-gravity [type="checkbox"]', textContainer)
          .prop('checked', ts.gravity === GRAVITY.CENTER);

        $('.gravity-row', textContainer).removeClass('d-none');
      }
    });
  };

  let restoreMainTextArea = () => {
    const storedTextAreas = App.pageData('currenteditedcontent.textAreas') || [];
    if (!storedTextAreas?.length) {
      textStates = [];
      return;
    }

    textStates = storedTextAreas.map((sta) => ({
      fontSize: getMaxFontSize({
        fontSize: sta.fontSize,
        fontFamily: sta.fontFamily,
        text: sta.text,
      }) || defaultTextState.fontSize,
      gravity: sta.gravity,
      isPersonalization: sta.isPersonalization,
      text: sta.text,
      fontFamily: sta.fontFamily,
      fontColor: sta.fontColor,
      ...getOffsetSize(sta.offset, sta.widthHeight, defaultTextState),
      moved: false,
    }));
  };

  let shrinkTextState = () => {
    const currentArea = getCurrentTemplatePreviewArea();
    const parentArea = getParentTemplatePreviewArea();

    if (!parentArea) {
      return;
    }

    const widthRatio = currentArea.width / parentArea.width;
    const heightRatio = currentArea.height / parentArea.height;

    const ratio = Math.min(widthRatio, heightRatio);

    textStates = textStates?.map((ts) => ({
      ...ts,
      fontSize: Math.floor(ts.fontSize * ratio),
      x: Math.round(ts.x * ratio),
      y: Math.round(ts.y * ratio),
      w: ts.w === null ? null : Math.round(ts.w * ratio),
      h: ts.h === null ? null : Math.round(ts.h * ratio),
    })) || [];
  };

  let restoreTextAreaForTemplate = (templateName = state.template?.templateName) => {
    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};
    const properties = templatesProperties[templateName];
    const parentTemplate = getParentTemplate();

    if (!properties?.textProperties?.length) {
      if (parentTemplate && parentTemplate?.templateName !== templateName) {
        restoreTextAreaForTemplate(parentTemplate.templateName);
        return;
      }

      restoreMainTextArea();
      return;
    }

    properties.textProperties.forEach((tp, index) => {
      textStates[index] = {
        ...getTextOffsetSize(tp, textStates[index], templateName),
        moved: true,
      };
    });

    if (parentTemplate && parentTemplate?.templateName === templateName) {
      shrinkTextState();
    }
  };

  let getTextWidthHeight = ({ fontSize, fontFamily, text }) => {
    const textElement = $('<div></div>')
      .css('display', 'none')
      .css('align-self', 'self-start')
      .css('text-wrap', 'nowrap')
      .css('line-height', 1.5)
      .css('font-size', `${fontSize}px`)
      .css('font-family', fontFamily)
      .text(text);

    $('body').append(textElement);
    const textWidth = textElement.width();
    const textHeight = textElement.height();
    textElement.remove();

    return {
      w: Math.round(textWidth),
      h: Math.round(textHeight),
    };
  };

  let getMaxFontSize = ({ fontSize, fontFamily, text, templateName }) => {
    const currentArea = templateName ? getTemplatePreviewArea(templateName) : getCurrentTemplatePreviewArea();

    if (!currentArea) {
      return defaultTextState.fontSize;
    }

    const { w: textWidth, h: textHeight } = getTextWidthHeight({ fontSize, fontFamily, text });

    if (textWidth < currentArea.width && textHeight < currentArea.height) {
      return fontSize;
    }

    const widthRatio = currentArea.width / textWidth;
    const heightRatio = currentArea.height / textHeight;

    if (textWidth > currentArea.width && textHeight > currentArea.height) {
      return Math.floor(fontSize * Math.min(widthRatio, heightRatio));
    }

    if (textWidth > currentArea.width) {
      return Math.floor(fontSize * widthRatio);
    }

    return Math.floor(fontSize * heightRatio);
  };

  let applyAll = (templateNames = []) => {
    const defaults = App.pageData('currenteditedcontent.defaults');
    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    const currentTemplate = state.template;
    const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;
    const currentPrintArea = currentTemplate.previewAreas[printAreaIndex];

    defaults?.AopTemplates?.filter(t => templateNames?.includes(t.templateName)).forEach(template => {
      template?.details?.forEach((t) => delete templatesProperties[t.templateName]);

      const widthRatio = template.previewAreas[printAreaIndex].width / currentPrintArea.width;
      const heightRatio = template.previewAreas[printAreaIndex].height / currentPrintArea.height;
      const ratio = Math.min(widthRatio, heightRatio);

      const clonedArtworkStates = (artworkStates || []).map((as) => ({
        x: Math.round(as.x * ratio),
        y: Math.round(as.y * ratio),
        w: !as.w ? null : Math.round(as.w * ratio),
        h: !as.h ? null : Math.round(as.h * ratio),
        a: as.a,
        imagePlacement: as.imagePlacement,
      }));

      templatesProperties[template.templateName] = {
        printArea: state.printArea,
        imageProperties: clonedArtworkStates.map((cas) => ({
          placement: cas.imagePlacement || '-1',
          widthHeight: cas.w !== null && cas.h !== null ? `${cas.w};${cas.h}` : null,
          offset: !cas.imagePlacement ? `${cas.x};${cas.y}` : '0;0',
          rotateValue: cas.a >= 0 ? cas.a : 360 + cas.a,
        })),
      };

      if (!textStates?.length) {
        return;
      }

      const clonedTextStates = textStates.map((ts) => ({
        fontSize: Math.floor(ts.fontSize * ratio),
        x: Math.round(ts.x * ratio),
        y: Math.round(ts.y * ratio),
        w: !ts.w ? null : Math.round(ts.w * ratio),
        h: !ts.h ? null : Math.round(ts.h * ratio),
      }));

      templatesProperties[template?.templateName] = {
        ...templatesProperties[template?.templateName],
        textProperties: clonedTextStates.map((cts) => ({
          fontSize: cts.fontSize,
          offset: `${cts.x};${cts.y}`,
          widthHeight: `${cts.w};${cts.h}`,
          previewHeight: state.template.previewHeight,
        }))
      };
    });

    App.pageData('currenteditedcontent.templatesProperties', templatesProperties);
  };

  let applyAllModal = () => {
    const defaults = App.pageData('currenteditedcontent.defaults');
    const template = require('./tmpl/dialog/apply-all-modal.hbs');

    const currentTemplate = state.template;
    const printAreaIndex = state.printArea === printArea[0].value ? 0 : 1;
    const currentPrintArea = currentTemplate.previewAreas[printAreaIndex];

    const ratio = currentPrintArea.width / currentPrintArea.height;

    bootbox.dialog({
      size: 'medium',
      title: $lang.artwork.complex.ApplyAll,
      message: template(defaults?.AopTemplates
        .filter(t => {
          const tRatio = t.previewAreas[printAreaIndex].width / t.previewAreas[printAreaIndex].height;

          return tRatio >= ratio - applyAllRatioDeviation && tRatio <= ratio + applyAllRatioDeviation;
        })),
      buttons: {
        Ok: {
          label: $lang.btn.applay,
          className: 'btn btn-brand',
          callback: () =>
            applyAll(Array.from($('#apply-all-modal input[type="checkbox"]:checked')).map((el) => el.value))
        },
      }
    });
  };

  let addArtworkToTemplatesProperties = () => {
    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    Object.keys(templatesProperties).forEach((key) => {
      templatesProperties[key].imageProperties ||= [];
      templatesProperties[key].imageProperties.push({
        placement: defaultArtworkState.imagePlacement,
        widthHeight: null,
        offset: '0;0',
        rotateValue: fixRotateValueByFlip(defaultArtworkState.a, key),
      });
    });

    App.pageData('currenteditedcontent.templatesProperties', templatesProperties);
  };

  let addTextToTemplatesProperties = () => {
    const templatesProperties = App.pageData('currenteditedcontent.templatesProperties') || {};

    Object.keys(templatesProperties).forEach((key) => {
      templatesProperties[key].textProperties ||= [];
      templatesProperties[key].textProperties.push({
        fontSize: getMaxFontSize({
          fontSize: defaultTextState.fontSize,
          text: defaultText,
          templateName: key,
        }) || defaultTextState.fontSize,
        offset: null,
        widthHeight: null,
        previewHeight: state.template.previewHeight,
      });
    });

    App.pageData('currenteditedcontent.templatesProperties', templatesProperties);
  };

  let loadFontForTextState = (textState) => {
    const fonts = App.pageData('currenteditedcontent.defaults.SystemFontFamilyCollection') || [];
    const fontUrl = fonts.find((f) => f.Font === textState.fontFamily)?.Url;

    if (!fontUrl) {
      return;
    }

    return loadFont(textState.fontFamily, fontUrl);
  };

  return {
    init,
    clear,
    isValid,
    generateFirstTemplateAsset,
  };
})();
